/**
 * 큐레이트 토픽 분류 — 단일 진실 공급원.
 *
 * `apps/web` 토픽 페이지(`/topics/[slug]/`) / 홈 칩 / Related Posts / JSON-LD keywords,
 * `apps/worker` 의 `/api/topics/warnings` 엔드포인트가 모두 이 파일을 참조한다.
 *
 * 정책:
 * - frontmatter `tags`는 LLM 출력 그대로 보존 (마이그레이션 X). 토픽 매핑은 빌드/요청 시 동적 계산.
 * - 새 태그가 누적되면 admin `/topics` 페이지에서 미매핑 태그 모니터링 (≥5 글) → 운영자가 본 파일에 추가.
 * - 태그 비교는 정규화(소문자 + 공백/대시/언더스코어 통합)로 case·spacing 차이 흡수.
 */

export interface Topic {
  /** URL slug (영문 kebab-case) */
  slug: string;
  /** 표시명 (페이지 헤더, 칩, JSON-LD keywords 사용) */
  displayName: string;
  /** SEO meta description */
  description: string;
  /** 본 토픽으로 매핑되는 source tags. 정규화된 비교. */
  tags: string[];
}

/** 토픽 정의 — 운영자가 직접 관리. 신설 / 매핑 추가는 이 배열만 수정. */
export const TOPICS: Topic[] = [
  {
    slug: 'ai',
    displayName: 'AI',
    description: 'AI · LLM · 머신러닝 동향. OpenAI, Anthropic, Google AI, 오픈 모델 등.',
    tags: [
      'AI',
      'LLM',
      'OpenAI',
      'Anthropic',
      'Claude',
      'Gemini',
      'Copilot',
      'Agent',
      'ML',
      '머신러닝',
      'AI윤리',
      'xAI',
      '프롬프트 엔지니어링',
      '하네스 엔지니어링',
      '벤치마크',
      'Humanoid',
      'ChatGPT',
    ],
  },
  {
    slug: 'bigtech',
    displayName: '빅테크',
    description: 'Apple · Google · Meta · Microsoft · 빅테크 기업 동향과 인사이트.',
    tags: [
      'Apple',
      'Google',
      'Meta',
      'Microsoft',
      'Amazon',
      'Netflix',
      'Spotify',
      '넷플릭스',
      '스포티파이',
      'Mac',
      'iPhone',
      'Google Photos',
      'Tim Cook',
      'Sam Altman',
      'Elon Musk',
      'Acquisition',
      'Partnership',
      '스트리밍',
      '숏폼',
    ],
  },
  {
    slug: 'devtools',
    displayName: '개발자 도구',
    description: '코드 에디터 · GitHub · DevOps · 개발 워크플로우 · 개발자 문화.',
    tags: [
      '개발도구',
      '개발자',
      '개발',
      '개발문화',
      '개발생산성',
      '개발환경',
      '코드 에디터',
      '코드품질',
      '코딩',
      'GitHub',
      'Copilot',
      'Cursor',
      'IDE',
      'DevOps',
      'CI/CD',
      'Bash',
      '버전관리',
      'Developer Experience',
      'Development Community',
      '디자인시스템',
    ],
  },
  {
    slug: 'opensource',
    displayName: '오픈소스',
    description: '오픈소스 프로젝트 · 커뮤니티 · 라이선스 동향.',
    tags: ['오픈소스', 'Open Source', 'Rust', 'Linux', 'Kernel'],
  },
  {
    slug: 'policy',
    displayName: '정책·법률',
    description: '소송 · 저작권 · 규제 · 프라이버시 · 검열 등 IT 정책 이슈.',
    tags: [
      'Lawsuit',
      'Legal',
      'Copyright',
      '소송',
      '지적재산권',
      '규제',
      '프라이버시',
      '검열',
    ],
  },
  {
    slug: 'ux',
    displayName: 'UX',
    description: 'UX · UI 디자인 · 사용자 경험 · 인터랙션 디자인 · 접근성.',
    tags: ['UX', 'UI', 'UI/UX', '사용자경험', '접근성', 'Accessibility', 'Design'],
  },
  {
    slug: 'productivity',
    displayName: '생산성',
    description: '업무 생산성 · 워크플로우 · 시간 관리 · 자동화 · 효율화 도구.',
    tags: ['생산성', 'Productivity', '워크플로우', '자동화', 'Automation'],
  },
  {
    slug: 'community',
    displayName: '커뮤니티',
    description: '개발자·온라인 커뮤니티 동향. Reddit, Hacker News, 오픈소스 커뮤니티 이슈.',
    tags: ['커뮤니티', 'Community'],
  },
  {
    slug: 'security',
    displayName: '보안',
    description: '사이버 보안 · 해킹 · 취약점 · 암호화 · 보안 사고 동향.',
    tags: ['보안', 'Security', '해킹', '취약점', 'Vulnerability', 'Hacking'],
  },
  {
    slug: 'infra',
    displayName: '인프라·클라우드',
    description: '데이터센터 · 클라우드 · 반도체 · 컴퓨트 · 호스팅 인프라.',
    tags: [
      '데이터센터',
      '에너지',
      '인프라',
      'Cloud',
      '클라우드',
      '컴퓨트',
      '반도체',
      '공급망',
      '웹호스팅',
    ],
  },
];

// ---------------------------------------------------------------------------
// 정규화 + 매핑 헬퍼
// ---------------------------------------------------------------------------

/**
 * 태그 비교용 정규화. 'Open Source' / 'open-source' / 'OpenSource' → 'open source'.
 * 한국어 태그는 그대로 (대소문자/공백 차이만 흡수).
 */
export function normalizeTag(tag: string): string {
  return tag.toLowerCase().trim().replace(/[\s\-_]+/g, ' ');
}

/** 모든 토픽의 매핑 태그를 정규화한 단일 Set. 미매핑 판정 시 사용. */
const MAPPED_TAG_SET: Set<string> = new Set(
  TOPICS.flatMap((t) => t.tags.map(normalizeTag)),
);

/** 글의 tags 배열 → 매칭되는 토픽들. 한 글이 여러 토픽에 동시 노출 가능. */
export function getTopicsForTags(tags: string[]): Topic[] {
  if (tags.length === 0) return [];
  const normalized = new Set(tags.map(normalizeTag));
  return TOPICS.filter((t) =>
    t.tags.some((mapped) => normalized.has(normalizeTag(mapped))),
  );
}

/** slug로 토픽 조회. `getStaticPaths` 등에 사용. */
export function getTopicBySlug(slug: string): Topic | undefined {
  return TOPICS.find((t) => t.slug === slug);
}

// ---------------------------------------------------------------------------
// 모니터링 (admin /topics 페이지가 사용)
// ---------------------------------------------------------------------------

/**
 * 미매핑 태그를 글 수와 함께 반환. 임계 이상만 필터.
 * 동일 정규화 형태(`Open Source` / `open-source`)는 합산되며, 표시명은 첫 등장한 원본을 사용.
 */
export function getUnmappedTagsAboveThreshold(
  postsTags: string[][],
  threshold = 5,
): Array<{ tag: string; count: number }> {
  type Entry = { count: number; displayTag: string };
  const counts = new Map<string, Entry>();

  for (const postTags of postsTags) {
    const seenInPost = new Set<string>();
    for (const tag of postTags) {
      const norm = normalizeTag(tag);
      if (MAPPED_TAG_SET.has(norm)) continue;
      if (seenInPost.has(norm)) continue;
      seenInPost.add(norm);
      const existing = counts.get(norm);
      if (existing) {
        existing.count += 1;
      } else {
        counts.set(norm, { count: 1, displayTag: tag });
      }
    }
  }

  return [...counts.values()]
    .filter((e) => e.count >= threshold)
    .map(({ displayTag, count }) => ({ tag: displayTag, count }))
    .sort((a, b) => b.count - a.count);
}

/** 토픽별 글 수 분포. admin 페이지에서 매핑 정합성 확인용. */
export function getTopicDistribution(
  postsTags: string[][],
): Array<{ slug: string; displayName: string; count: number }> {
  const counts = new Map<string, number>();
  for (const postTags of postsTags) {
    const topics = getTopicsForTags(postTags);
    for (const t of topics) {
      counts.set(t.slug, (counts.get(t.slug) ?? 0) + 1);
    }
  }
  return TOPICS.map((t) => ({
    slug: t.slug,
    displayName: t.displayName,
    count: counts.get(t.slug) ?? 0,
  }));
}
